// src/components/Featured.jsx
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../supabase/axios";
import { toast } from "react-toastify";
import { useCurrency } from "../supabase/CurrencyContext";

function Featured() {
  const navigate = useNavigate();

  const {
    currency,
    loading: currencyLoading,
    convertFromINR,
    baseCurrency = "INR",
  } = useCurrency();

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLatestProducts = async () => {
    try {
      // ✅ correct endpoint name
      const response = await api.get("/api/products/getLatestProducts");

      console.log(
        "API DATA:",
        response.data.map((p) => ({
          id: p.ProductId,
          name: p.Name,
        }))
      );

      setProducts((response.data || []).slice(0, 6));
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.message || "Failed to fetch latest products");
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currencyLoading) return;
    fetchLatestProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, currencyLoading]);

  // ✅ FIX 1: image_url is the correct key from your DB
  const getImageUrl = (product) => {
    const images = product.product_images;
    if (!images || images.length === 0) return null;
    const hero = images.find((img) => img.is_hero === true) || images[0];
    if (typeof hero === "string") return hero;
    return hero?.image_url || null;
  };

  const formatPrice = (value, currencyCode) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  };

  const SkeletonCard = () => (
    <div className="animate-pulse">
      <div className="aspect-[3/4] bg-[#EEE6E1]"></div>
      <div className="py-5 text-center">
        <div className="h-3 bg-[#4B2C20]/15 w-1/2 mx-auto mb-2"></div>
        <div className="h-3 bg-[#4B2C20]/10 w-1/4 mx-auto"></div>
      </div>
    </div>
  );

  const showSkeletons = isLoading || currencyLoading || products.length === 0;

  return (
    <section className="bg-[#F1E7E5] text-center relative overflow-hidden">

      {/* Heading — padded */}
      <div className="py-20 px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-bold mb-3 text-[#4B2C20]"
        >
          Featured Treasures
        </motion.h2>
        <p className="text-[#4B2C20]/70 mb-0 max-w-xl mx-auto">
          Handpicked vintage pieces that capture timeless style and craftsmanship.
        </p>
      </div>

      {/* ✅ Prada-style: full bleed, no gaps between images */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {showSkeletons ? (
          Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          products.map((product, i) => {
            console.log(
  "RENDERING:",
  product.ProductId,
  product.Name
);
            const basePriceINR = Number(product.Price || 0);
            // ✅ FIX 2: correct field name from your DB is DiscountPrice, not old_price
            const baseOldPriceINR = Number(product.DiscountPrice || 0);

            const displayPrice = convertFromINR
              ? convertFromINR(basePriceINR)
              : basePriceINR;

            const displayOldPrice =
              baseOldPriceINR > 0
                ? convertFromINR
                  ? convertFromINR(baseOldPriceINR)
                  : baseOldPriceINR
                : null;

            const imageUrl = getImageUrl(product);

            return (
              <motion.div
                key={product.ProductId}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="group cursor-pointer"
                onClick={() => navigate(`/products/${product.ProductId}`)}
              >
                {/* IMAGE — flush edge-to-edge, no gap, no radius */}
                <div className="h-[720px] overflow-hidden bg-[#EDE5E0]">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={product.Name}
                       className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#E5DBD6]" />
                  )}
                </div>

                {/* TEXT below image */}
                <div className="py-5 px-3 text-center bg-[#F1E7E5]">
                  <h3 className="text-[13px] font-normal text-[#1a1a1a] tracking-wide mb-1">
                    {product.Name}
                  </h3>
                  <div className="text-[13px] text-[#4B2C20] font-light">
                    {formatPrice(displayPrice, currency || baseCurrency)}
                  </div>
                  {displayOldPrice && (
                    <div className="text-xs text-[#6B5A52] line-through mt-0.5">
                      {formatPrice(displayOldPrice, currency || baseCurrency)}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Button — padded */}
      <div className="py-16">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/products")}
          className="bg-[#4B2C20] text-white px-8 py-3 rounded-full shadow hover:bg-[#6B4226] transition"
        >
          View All Products
        </motion.button>
      </div>


    </section>
  );
}



export default Featured;